import React, { useState } from 'react';
import { X, Package, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import LockerGrid from '../components/LockerGrid';
import { useAuth } from '../context/AuthContext';
import { getTranslation } from '../i18n/translations';
import { toast } from '../hooks/use-toast';

const Lockers = () => {
  const { lockers, whiteLabel, depositParcel, retrieveParcel, simulateEvent, generatePin, language } = useAuth();
  const primaryColor = whiteLabel ? '#2D4A8F' : '#F26724';
  const t = (key, replacements) => getTranslation(key, language, replacements);
  
  const [selectedLocker, setSelectedLocker] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleLockerClick = (locker) => {
    setSelectedLocker(locker);
    setShowModal(true);
  };

  const handleSimulateDrop = () => {
    if (selectedLocker.state !== 'Free') {
      toast({
        title: t('cannotDeposit'),
        description: t('lockerNotAvailable'),
        variant: 'destructive'
      });
      return;
    }

    const pin = generatePin();
    depositParcel(selectedLocker.id, selectedLocker.size, pin, 'Simulated User');
    toast({
      title: t('dropSimulated'),
      description: t('parcelDepositedInLocker', { locker: selectedLocker.id, pin }),
    });
    setShowModal(false);
  };

  const handleSimulateRetrieve = () => {
    if (selectedLocker.state !== 'Occupied') {
      toast({
        title: t('cannotRetrieve'),
        description: t('lockerIsEmpty'),
        variant: 'destructive'
      });
      return;
    }

    retrieveParcel(selectedLocker.id, 'Simulation', selectedLocker.user || 'User');
    toast({
      title: t('retrievalSimulated'),
      description: t('lockerNowFree', { locker: selectedLocker.id }),
    });
    setShowModal(false);
  };

  const handleSimulateTamper = () => {
    simulateEvent('Tamper', selectedLocker.id, { alert: 'Door sensor triggered' });
    toast({
      title: t('tamperAlert'),
      description: t('tamperDetected', { locker: selectedLocker.id }),
      variant: 'destructive'
    });
    setShowModal(false);
  };

  const handleSimulateBatteryLow = () => {
    simulateEvent('Battery-low', selectedLocker.id, { alert: 'Battery below 20%' });
    toast({
      title: t('batteryAlert'),
      description: t('lowBatteryDetected', { locker: selectedLocker.id }),
      variant: 'destructive'
    });
    setShowModal(false);
  };

  const freeCount = lockers.filter(l => l.state === 'Free').length;
  const occupiedCount = lockers.filter(l => l.state === 'Occupied').length;
  const alertCount = lockers.filter(l => l.state === 'Alert').length;

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-4xl font-bold text-[#1A1A1A]">{t('lockerMap')}</h1>
        <p className="text-gray-600">{t('realTimeStatus')}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t('freeLockers')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-green-600">{freeCount}</div>
            <p className="text-sm text-gray-500 mt-1">{t('availableForUse')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t('occupiedLockers')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold" style={{ color: primaryColor }}>{occupiedCount}</div>
            <p className="text-sm text-gray-500 mt-1">{t('currentlyInUse')}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{t('alerts')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-yellow-600">{alertCount}</div>
            <p className="text-sm text-gray-500 mt-1">{t('requireAttention')}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('lockerGrid')}</CardTitle>
          <CardDescription>{t('clickForDetails')}</CardDescription>
        </CardHeader>
        <CardContent>
          <LockerGrid onLockerClick={handleLockerClick} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('legend')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-green-100 border-2 border-green-600"></div>
              <span className="text-sm text-gray-700">{t('freeAvailable')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded border-2" style={{ backgroundColor: `${primaryColor}20`, borderColor: primaryColor }}></div>
              <span className="text-sm text-gray-700">{t('occupiedContains')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-yellow-100 border-2 border-yellow-600"></div>
              <span className="text-sm text-gray-700">{t('alertRequired')}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl">{t('locker')} #{selectedLocker?.id}</DialogTitle>
            <DialogDescription>
              {t('manageSimulateActions')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-lg bg-gray-50">
                <p className="text-xs text-gray-600 mb-1">{t('size')}</p>
                <p className="text-2xl font-bold text-[#1A1A1A]">{selectedLocker?.size}</p>
              </div>
              <div className="p-4 rounded-lg bg-gray-50">
                <p className="text-xs text-gray-600 mb-1">{t('status')}</p>
                <p className="text-2xl font-bold" style={{
                  color: selectedLocker?.state === 'Free' ? '#10B981' : 
                         selectedLocker?.state === 'Occupied' ? primaryColor : '#F59E0B'
                }}>
                  {selectedLocker?.state}
                </p>
              </div>
            </div>

            {selectedLocker?.user && (
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                <p className="text-xs text-gray-600 mb-1">{t('currentUser')}</p>
                <p className="text-lg font-semibold text-[#1A1A1A]">{selectedLocker.user}</p>
                {selectedLocker.pin && (
                  <p className="text-sm text-gray-600 mt-2">{t('pin')}: {selectedLocker.pin}</p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Button
                onClick={handleSimulateDrop}
                disabled={selectedLocker?.state !== 'Free'}
                className="w-full"
                style={{ backgroundColor: primaryColor }}
              >
                <Package className="mr-2" size={16} />
                {t('simulateDrop')}
              </Button>
              
              <Button
                onClick={handleSimulateRetrieve}
                disabled={selectedLocker?.state !== 'Occupied'}
                variant="outline"
                className="w-full"
              >
                {t('simulateRetrieve')}
              </Button>
              
              <Button
                onClick={handleSimulateTamper}
                variant="outline"
                className="w-full text-yellow-700 border-yellow-400 hover:bg-yellow-50"
              >
                <AlertTriangle className="mr-2" size={16} />
                {t('simulateTamper')}
              </Button>
              
              <Button
                onClick={handleSimulateBatteryLow}
                variant="outline"
                className="w-full text-orange-700 border-orange-400 hover:bg-orange-50"
              >
                {t('simulateBatteryLow')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Lockers;
